import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiCreditCard, FiStar, FiZap } = FiIcons;

const PricingCards = () => {
  const plans = [
    {
      name: "Basic",
      amount: 49,
      priceId: "price_1SRl6dEWGT02FQpCssN3fJBs",
      paymentLink: "https://buy.stripe.com/6oU00j1qw0Ue480cZtao80t",
      currency: "usd",
      description: "Perfect for individual practitioners getting started",
      features: [
        "Up to 50 referrals/month",
        "Basic analytics dashboard", 
        "Email support",
        "Community access",
        "10GB storage"
      ],
      popular: false,
      color: "blue"
    },
    {
      name: "Pro",
      amount: 99,
      priceId: "price_1SRl6dEWGT02FQpCqTjnvp3R",
      paymentLink: "https://buy.stripe.com/28E00jb168mG33W9Nhao80u",
      currency: "usd",
      description: "Ideal for growing practices",
      features: [
        "Unlimited referrals",
        "Advanced analytics",
        "Priority support",
        "Dealrooms access",
        "100GB storage",
        "API access",
        "Custom reporting"
      ],
      popular: true,
      color: "purple"
    },
    {
      name: "Enteerprise",
      amount: 299,
      priceId: "price_1SRl6dEWGT02FQpCWU4TReYS",
      paymentLink: "https://buy.stripe.com/3cI9ATfhm8mGcEw1gLao80v",
      currency: "usd",
      description: "For large practices and DSOs",
      features: [
        "Everything in Pro",
        "Unlimited storage",
        "White-label branding",
        "Dedicated account manager",
        "Custom integrations",
        "Advanced security",
        "Multi-location support",
        "Training & onboarding"
      ],
      popular: false,
      color: "green"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        button: 'bg-green-600 hover:bg-green-700'
      }
    };
    return colors[color] || colors.blue;
  };

  const handlePaymentClick = (paymentLink) => {
    window.open(paymentLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the perfect plan for your dental practice. All plans include our core features with different usage limits and support levels.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const colorClasses = getColorClasses(plan.color);
            
            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden hover:shadow-lg transition-all duration-300 ${
                  plan.popular ? 'border-purple-500 ring-2 ring-purple-500 ring-opacity-20 scale-105' : colorClasses.border
                }`}
              >
                {plan.popular && (
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-center py-3">
                    <div className="flex items-center justify-center space-x-2">
                      <SafeIcon icon={FiStar} className="w-4 h-4" />
                      <span className="text-sm font-medium">Most Popular</span>
                      <SafeIcon icon={FiStar} className="w-4 h-4" />
                    </div>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${colorClasses.bg}`}>
                      <SafeIcon 
                        icon={plan.name === 'Pro' ? FiZap : FiCreditCard} 
                        className={`w-6 h-6 ${colorClasses.text}`} 
                      />
                    </div>
                    <h3 className={`text-2xl font-bold mb-2 ${colorClasses.text}`}>
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        ${plan.amount}
                      </span>
                      <span className="text-gray-600 ml-2">/month</span>
                    </div>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <SafeIcon 
                          icon={FiCheck} 
                          className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" 
                        />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handlePaymentClick(plan.paymentLink)}
                    className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 flex items-center justify-center space-x-2 ${colorClasses.button} hover:shadow-md`}
                  >
                    <span>Get Started</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-3">
                    No setup fees • Cancel anytime
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I change plans later?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated automatically.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Is there a free trial?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes, all plans come with a 14-day free trial. No credit card required to start.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600 text-sm">
                We accept all major credit cards, PayPal, and bank transfers for Enterprise plans.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes, we offer a 30-day money-back guarantee for all plans. No questions asked.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingCards;